import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';

import { LIMIT_FOR_FETCHING_ADMIN_ITEMS } from '~/shared/constants';
import { getPaginationActions } from '~/shared/helpers/redux-helpers';
import type { FileType } from '~/types';

export const pageInfo = {
  hasPreviousPage: false,
  hasNextPage: false,
  currentPage: 1,
  perPage: LIMIT_FOR_FETCHING_ADMIN_ITEMS,
  itemCount: 0,
  pageCount: 1,
};

export const pagesInitialState = {
  items: [],
  isLoading: false,
  pageInfo,
};

type CoursesSliceType = {
  lessonExerciseFile: FileType | null;
  exerciseForSelectedFiles: FileType | null;
  isMoveFilesBlock: boolean;
  isMoveToExerciseFilesBlock: boolean;
  preparedSharedFiles: FileType[];
  preparedPersonalFiles: FileType[];
  personalFilesPagination: {
    items: Record<string, unknown>[];
    pageInfo: Record<string, unknown>;
    isLoading: boolean;
  };
  sharedFilesPagination: {
    items: Record<string, unknown>[];
    pageInfo: Record<string, unknown>;
    isLoading: boolean;
  };
};

const initialState: CoursesSliceType = {
  lessonExerciseFile: null,
  exerciseForSelectedFiles: null,
  isMoveFilesBlock: false,
  isMoveToExerciseFilesBlock: false,
  preparedSharedFiles: [],
  preparedPersonalFiles: [],
  personalFilesPagination: pagesInitialState,
  sharedFilesPagination: pagesInitialState,
};

export const filesSlice = createSlice({
  name: 'files',
  initialState,
  reducers: {
    setPreparedSharedFiles: (state, action: PayloadAction<FileType | null>) => {
      state.preparedSharedFiles = action.payload;
    },
    setPreparedPersonalFiles: (state, action: PayloadAction<FileType | null>) => {
      state.preparedPersonalFiles = action.payload;
    },
    setLessonExerciseFile: (state, action: PayloadAction<FileType | null>) => {
      state.lessonExerciseFile = action.payload;
    },
    setIsMoveFilesBlock: (state, action: PayloadAction<FileType | null>) => {
      state.isMoveFilesBlock = action.payload;
    },
    setIsMoveToExerciseFilesBlock: (state, action: PayloadAction<FileType | null>) => {
      state.isMoveToExerciseFilesBlock = action.payload;
    },
    setPersonalFilesPagination: (state, action: PayloadAction<string>) => {
      state.personalFilesPagination = action.payload;
    },
    setExerciseForSelectedFiles: (state, action: PayloadAction<string>) => {
      state.exerciseForSelectedFiles = action.payload;
    },
    ...getPaginationActions('personalFilesPagination', 'preparedPersonalFiles'),
    setSharedFilesPagination: (state, action: PayloadAction<string>) => {
      state.sharedFilesPagination = action.payload;
    },
    ...getPaginationActions('sharedFilesPagination', 'preparedSharedFiles'),
  },
});

export const {
  setPreparedSharedFiles,
  setPreparedPersonalFiles,
  setPersonalFilesPagination,
  setSharedFilesPagination,
  setIsMoveFilesBlock,
  setExerciseForSelectedFiles,
  appendPersonalFilesPagination,
  setIsMoveToExerciseFilesBlock,
  setPersonalFilesPaginationLoading,
  appendSharedFilesPagination,
  setSharedFilesPaginationLoading,
  setLessonExerciseFile,
} = filesSlice.actions;
